import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { zonesAPI, dashboardAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useSocketStore } from '../stores/socketStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  MapIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  PlusIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import ZoneFormModal from '../components/zones/ZoneFormModal';

const zoneTypes = {
  general: { label: 'General Area', color: 'bg-blue-500', textColor: 'text-blue-400', hex: '#3B82F6' },
  restricted: { label: 'Restricted Zone', color: 'bg-red-500', textColor: 'text-red-400', hex: '#EF4444' },
  hazard: { label: 'Hazard Zone', color: 'bg-orange-500', textColor: 'text-orange-400', hex: '#F97316' },
  crane_zone: { label: 'Crane Zone', color: 'bg-purple-500', textColor: 'text-purple-400', hex: '#8B5CF6' },
  excavation: { label: 'Excavation', color: 'bg-yellow-500', textColor: 'text-yellow-400', hex: '#EAB308' },
  chemical: { label: 'Chemical Handling', color: 'bg-pink-500', textColor: 'text-pink-400', hex: '#EC4899' },
  electrical: { label: 'Electrical Hazard', color: 'bg-amber-500', textColor: 'text-amber-400', hex: '#F59E0B' },
  confined_space: { label: 'Confined Space', color: 'bg-gray-500', textColor: 'text-gray-400', hex: '#6B7280' },
  hot_work: { label: 'Hot Work Zone', color: 'bg-red-600', textColor: 'text-red-400', hex: '#DC2626' },
  safe_zone: { label: 'Safe Zone', color: 'bg-green-500', textColor: 'text-green-400', hex: '#22C55E' },
};

/**
 * Extract polygon points from a zone's polygon data.
 * Handles multiple formats:
 * - Array of {x, y} with values 0-100 (percentage-based from CameraConfigPage)
 * - { type: 'polygon', coordinates: [...] } with nested arrays of {x,y} or [x,y]
 * - Array of [x, y] pairs
 * Returns normalized points as [{x, y}] where x and y are in the 0-100 range.
 */
function extractPolygonPoints(polygon) {
  if (!polygon) return [];

  let points = [];

  if (Array.isArray(polygon)) {
    points = polygon;
  } else if (polygon.coordinates && Array.isArray(polygon.coordinates)) {
    // { type: 'polygon', coordinates: [...] }
    const coords = polygon.coordinates;
    // GeoJSON-style nested array: [[x,y], [x,y], ...]
    if (coords.length > 0 && Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
      // GeoJSON Polygon: [[[x,y],[x,y],...]]
      points = coords[0].map(c => ({ x: c[0], y: c[1] }));
    } else if (coords.length > 0 && Array.isArray(coords[0])) {
      // Simple array of [x,y] pairs
      points = coords.map(c => ({ x: c[0], y: c[1] }));
    } else {
      // Array of {x, y} objects
      points = coords;
    }
  } else if (polygon.points && Array.isArray(polygon.points)) {
    points = polygon.points;
  }

  // Normalize: ensure each point has x and y as numbers
  return points
    .filter(p => p && (typeof p.x === 'number' || typeof p[0] === 'number'))
    .map(p => {
      const x = typeof p.x === 'number' ? p.x : p[0];
      const y = typeof p.y === 'number' ? p.y : p[1];
      return { x, y };
    });
}

/**
 * SVG-based zone polygon preview component.
 * Draws the zone boundary on a grid background with a semi-transparent fill.
 */
function ZonePolygonPreview({ zone, width = 200, height = 130, showLabel = true, className = '' }) {
  const points = extractPolygonPoints(zone.polygon);
  const typeInfo = zoneTypes[zone.type] || zoneTypes.general;
  const fillColor = zone.color || typeInfo.hex || '#3B82F6';

  if (points.length < 3) {
    return (
      <div
        className={clsx('flex items-center justify-center bg-slate-100 rounded-lg border border-slate-200', className)}
        style={{ width, height }}
      >
        <div className="text-center">
          <MapIcon className="w-6 h-6 text-slate-300 mx-auto mb-1" />
          <p className="text-xs text-slate-400">No polygon</p>
        </div>
      </div>
    );
  }

  // Determine if points are in 0-100 range or 0-1 range
  const maxVal = Math.max(...points.map(p => Math.max(p.x, p.y)));
  const scale = maxVal <= 1.0 ? 100 : 1; // If max is <= 1, treat as normalized 0-1

  // Scale points to SVG viewBox (0-100)
  const scaledPoints = points.map(p => ({
    x: p.x * scale,
    y: p.y * scale,
  }));

  // Build SVG polygon points string
  const svgPoints = scaledPoints.map(p => `${p.x},${p.y}`).join(' ');

  // Calculate centroid for label placement
  const centroidX = scaledPoints.reduce((sum, p) => sum + p.x, 0) / scaledPoints.length;
  const centroidY = scaledPoints.reduce((sum, p) => sum + p.y, 0) / scaledPoints.length;

  return (
    <div
      className={clsx('relative rounded-lg overflow-hidden border border-slate-200', className)}
      style={{ width, height }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="w-full h-full"
        style={{ background: '#F1F5F9' }}
      >
        {/* Grid pattern */}
        <defs>
          <pattern id={`grid-${zone.id}`} width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#CBD5E1" strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill={`url(#grid-${zone.id})`} />

        {/* Polygon fill */}
        <polygon
          points={svgPoints}
          fill={fillColor}
          fillOpacity="0.2"
          stroke={fillColor}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />

        {/* Vertex dots */}
        {scaledPoints.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="1.5"
            fill={fillColor}
            stroke="white"
            strokeWidth="0.5"
          />
        ))}

        {/* Zone name label */}
        {showLabel && (
          <>
            <rect
              x={centroidX - 18}
              y={centroidY - 4}
              width="36"
              height="8"
              rx="2"
              fill="rgba(0,0,0,0.6)"
            />
            <text
              x={centroidX}
              y={centroidY + 2}
              textAnchor="middle"
              fill="white"
              fontSize="4"
              fontWeight="600"
              fontFamily="system-ui, sans-serif"
            >
              {zone.name.length > 12 ? zone.name.substring(0, 12) + '...' : zone.name}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

/**
 * Larger polygon detail view used in the zone detail modal.
 * Draws the polygon with more detail, including vertex indices and dimensions.
 */
function ZonePolygonDetail({ zone }) {
  const points = extractPolygonPoints(zone.polygon);
  const typeInfo = zoneTypes[zone.type] || zoneTypes.general;
  const fillColor = zone.color || typeInfo.hex || '#3B82F6';

  if (points.length < 3) {
    return (
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
        <h4 className="font-medium text-slate-800 mb-3">Zone Boundary</h4>
        <div className="flex items-center justify-center py-6">
          <div className="text-center">
            <MapIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No polygon boundary defined</p>
            <p className="text-xs text-slate-400 mt-1">
              Assign this zone to a camera to define its boundary
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Determine if points are in 0-100 range or 0-1 range
  const maxVal = Math.max(...points.map(p => Math.max(p.x, p.y)));
  const scale = maxVal <= 1.0 ? 100 : 1;

  const scaledPoints = points.map(p => ({
    x: p.x * scale,
    y: p.y * scale,
  }));

  const svgPoints = scaledPoints.map(p => `${p.x},${p.y}`).join(' ');

  const centroidX = scaledPoints.reduce((sum, p) => sum + p.x, 0) / scaledPoints.length;
  const centroidY = scaledPoints.reduce((sum, p) => sum + p.y, 0) / scaledPoints.length;

  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-slate-800">Zone Boundary</h4>
        <span className="text-xs text-slate-500">{points.length} vertices</span>
      </div>
      <div className="rounded-lg overflow-hidden border border-slate-200">
        <svg
          viewBox="-2 -2 104 104"
          preserveAspectRatio="xMidYMid meet"
          className="w-full"
          style={{ background: '#0F172A', maxHeight: '240px' }}
        >
          {/* Grid */}
          <defs>
            <pattern id={`detail-grid-${zone.id}`} width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#1E293B" strokeWidth="0.3" />
            </pattern>
          </defs>
          <rect x="-2" y="-2" width="104" height="104" fill={`url(#detail-grid-${zone.id})`} />

          {/* Polygon fill with glow effect */}
          <polygon
            points={svgPoints}
            fill={fillColor}
            fillOpacity="0.15"
            stroke={fillColor}
            strokeWidth="1"
            strokeLinejoin="round"
          />
          {/* Brighter border stroke */}
          <polygon
            points={svgPoints}
            fill="none"
            stroke={fillColor}
            strokeWidth="0.8"
            strokeLinejoin="round"
            strokeDasharray="none"
            opacity="0.8"
          />

          {/* Edge lines with subtle dash for direction */}
          {scaledPoints.map((p, i) => {
            const next = scaledPoints[(i + 1) % scaledPoints.length];
            return (
              <line
                key={`edge-${i}`}
                x1={p.x}
                y1={p.y}
                x2={next.x}
                y2={next.y}
                stroke={fillColor}
                strokeWidth="1.2"
                strokeOpacity="0.9"
              />
            );
          })}

          {/* Vertex dots with index labels */}
          {scaledPoints.map((p, i) => (
            <g key={`vertex-${i}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r="2"
                fill={fillColor}
                stroke="white"
                strokeWidth="0.6"
              />
              <text
                x={p.x + 3}
                y={p.y - 2}
                fill="#94A3B8"
                fontSize="3"
                fontFamily="system-ui, sans-serif"
              >
                {i + 1}
              </text>
            </g>
          ))}

          {/* Zone name label at centroid */}
          <rect
            x={centroidX - 20}
            y={centroidY - 4.5}
            width="40"
            height="9"
            rx="2"
            fill="rgba(0,0,0,0.7)"
          />
          <text
            x={centroidX}
            y={centroidY + 2}
            textAnchor="middle"
            fill="white"
            fontSize="4.5"
            fontWeight="600"
            fontFamily="system-ui, sans-serif"
          >
            {zone.name.length > 16 ? zone.name.substring(0, 16) + '...' : zone.name}
          </text>
        </svg>
      </div>
    </div>
  );
}

/**
 * Overview map that renders all zone polygons on a single SVG canvas.
 * Allows users to see where all zones are relative to each other.
 */
function ZonesOverviewMap({ zones, onSelectZone }) {
  const [hoveredZone, setHoveredZone] = useState(null);

  const zonesWithPolygons = zones
    .map(zone => ({
      ...zone,
      polygonPoints: extractPolygonPoints(zone.polygon),
    }))
    .filter(z => z.polygonPoints.length >= 3);

  if (zonesWithPolygons.length === 0) return null;

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-800">Zone Map Overview</h3>
          <p className="text-sm text-slate-500">
            {zonesWithPolygons.length} zone{zonesWithPolygons.length !== 1 ? 's' : ''} with defined boundaries
          </p>
        </div>
        <div className="flex items-center gap-2">
          <EyeIcon className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-400">Click a zone to view details</span>
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-slate-200">
        <svg
          viewBox="-5 -5 110 110"
          preserveAspectRatio="xMidYMid meet"
          className="w-full"
          style={{ background: '#0F172A', maxHeight: '380px' }}
        >
          {/* Grid */}
          <defs>
            <pattern id="overview-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#1E293B" strokeWidth="0.2" />
            </pattern>
            {/* Glow filter for hovered zone */}
            <filter id="glow">
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <rect x="-5" y="-5" width="110" height="110" fill="url(#overview-grid)" />

          {/* Axis labels */}
          <text x="50" y="-1" textAnchor="middle" fill="#475569" fontSize="2.5" fontFamily="system-ui, sans-serif">Camera View</text>
          <line x1="0" y1="0" x2="100" y2="0" stroke="#334155" strokeWidth="0.3" />
          <line x1="0" y1="0" x2="0" y2="100" stroke="#334155" strokeWidth="0.3" />
          <line x1="100" y1="0" x2="100" y2="100" stroke="#334155" strokeWidth="0.3" />
          <line x1="0" y1="100" x2="100" y2="100" stroke="#334155" strokeWidth="0.3" />

          {/* Render all zone polygons */}
          {zonesWithPolygons.map(zone => {
            const typeInfo = zoneTypes[zone.type] || zoneTypes.general;
            const fillColor = zone.color || typeInfo.hex || '#3B82F6';
            const points = zone.polygonPoints;
            const maxVal = Math.max(...points.map(p => Math.max(p.x, p.y)));
            const scale = maxVal <= 1.0 ? 100 : 1;
            const scaledPoints = points.map(p => ({ x: p.x * scale, y: p.y * scale }));
            const svgPoints = scaledPoints.map(p => `${p.x},${p.y}`).join(' ');
            const centroidX = scaledPoints.reduce((sum, p) => sum + p.x, 0) / scaledPoints.length;
            const centroidY = scaledPoints.reduce((sum, p) => sum + p.y, 0) / scaledPoints.length;
            const isHovered = hoveredZone === zone.id;

            return (
              <g
                key={zone.id}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredZone(zone.id)}
                onMouseLeave={() => setHoveredZone(null)}
                onClick={() => onSelectZone(zone)}
                filter={isHovered ? 'url(#glow)' : undefined}
              >
                {/* Zone polygon */}
                <polygon
                  points={svgPoints}
                  fill={fillColor}
                  fillOpacity={isHovered ? 0.35 : 0.2}
                  stroke={fillColor}
                  strokeWidth={isHovered ? 1.2 : 0.8}
                  strokeLinejoin="round"
                />

                {/* Vertex dots */}
                {scaledPoints.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? 1.5 : 1}
                    fill={fillColor}
                    stroke="white"
                    strokeWidth="0.3"
                  />
                ))}

                {/* Zone label */}
                <rect
                  x={centroidX - 14}
                  y={centroidY - 3.5}
                  width="28"
                  height="7"
                  rx="1.5"
                  fill={isHovered ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.6)'}
                />
                <text
                  x={centroidX}
                  y={centroidY + 1.5}
                  textAnchor="middle"
                  fill="white"
                  fontSize="3"
                  fontWeight={isHovered ? '700' : '500'}
                  fontFamily="system-ui, sans-serif"
                >
                  {zone.name.length > 10 ? zone.name.substring(0, 10) + '..' : zone.name}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3">
        {zonesWithPolygons.map(zone => {
          const typeInfo = zoneTypes[zone.type] || zoneTypes.general;
          const fillColor = zone.color || typeInfo.hex || '#3B82F6';
          return (
            <button
              key={zone.id}
              className={clsx(
                'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors',
                hoveredZone === zone.id ? 'bg-slate-200' : 'hover:bg-slate-100'
              )}
              onMouseEnter={() => setHoveredZone(zone.id)}
              onMouseLeave={() => setHoveredZone(null)}
              onClick={() => onSelectZone(zone)}
            >
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: fillColor }}
              />
              <span className="text-slate-600 truncate max-w-[120px]">{zone.name}</span>
              <span className="text-slate-400">({typeInfo.label})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ZonesPage() {
  const { selectedSite } = useSiteStore();
  const { zoneOccupancy } = useSocketStore();
  const queryClient = useQueryClient();
  const [selectedZone, setSelectedZone] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [showInactiveZones, setShowInactiveZones] = useState(false);

  const { data: zonesData, isLoading } = useQuery({
    queryKey: ['zones', selectedSite?.id, showInactiveZones],
    queryFn: () => zonesAPI.getZones({
      siteId: selectedSite?.id,
      // Don't filter by isActive if showing inactive, otherwise only show active
      ...(showInactiveZones ? {} : { isActive: true }),
    }),
    enabled: !!selectedSite?.id,
  });

  const { data: occupancyData } = useQuery({
    queryKey: ['zone-occupancy', selectedSite?.id],
    queryFn: () => dashboardAPI.getZoneOccupancy(selectedSite?.id),
    enabled: !!selectedSite?.id,
    refetchInterval: 60000,
  });

  const deleteMutation = useMutation({
    mutationFn: (zoneId) => zonesAPI.deleteZone(zoneId),
    onSuccess: () => {
      toast.success('Zone deleted permanently');
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      setSelectedZone(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete zone');
    },
  });

  const softDeleteMutation = useMutation({
    mutationFn: (zoneId) => zonesAPI.updateZone(zoneId, { isActive: false }),
    onSuccess: () => {
      toast.success('Zone deactivated successfully');
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      setSelectedZone(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to deactivate zone');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (zoneId) => zonesAPI.updateZone(zoneId, { isActive: true }),
    onSuccess: () => {
      toast.success('Zone restored successfully');
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      setSelectedZone(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to restore zone');
    },
  });

  const zonesRaw = zonesData?.data;
  const zones = Array.isArray(zonesRaw) ? zonesRaw : (Array.isArray(zonesRaw?.data) ? zonesRaw.data : []);
  const occupancyRaw = occupancyData?.data;
  const occupancy = Array.isArray(occupancyRaw) ? occupancyRaw : (Array.isArray(occupancyRaw?.data) ? occupancyRaw.data : []);

  // Get real-time occupancy data from WebSocket for current site
  const realtimeOccupancy = selectedSite?.id ? zoneOccupancy[selectedSite.id] : null;

  const zonesWithOccupancy = zones.map(zone => {
    // Try to get real-time occupancy first, fall back to polled data
    let currentOccupancy = 0;
    if (realtimeOccupancy?.zones) {
      const rtZone = realtimeOccupancy.zones.find(z => z.zoneId === zone.id);
      currentOccupancy = rtZone?.occupancy ?? occupancy.find(o => o.id === zone.id)?.currentOccupancy ?? 0;
    } else {
      currentOccupancy = occupancy.find(o => o.id === zone.id)?.currentOccupancy ?? 0;
    }

    const isOvercrowded = zone.maxOccupancy ? currentOccupancy > zone.maxOccupancy : false;

    return {
      ...zone,
      currentOccupancy,
      isOvercrowded,
    };
  });

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSoftDelete = (zone) => {
    setDeleteTarget({
      zone,
      type: 'soft',
      title: 'Deactivate Zone',
      message: `Are you sure you want to deactivate "${zone.name}"? It can be restored later.`,
      confirmLabel: 'Deactivate',
      variant: 'warning',
    });
  };

  const handleHardDelete = (zone) => {
    setDeleteTarget({
      zone,
      type: 'hard',
      title: 'Delete Zone Permanently',
      message: `Are you sure you want to permanently delete "${zone.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete Permanently',
      variant: 'danger',
    });
  };

  const handleRestore = (zone) => {
    setDeleteTarget({
      zone,
      type: 'restore',
      title: 'Restore Zone',
      message: `Restore "${zone.name}" to active status?`,
      confirmLabel: 'Restore',
      variant: 'info',
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    switch (deleteTarget.type) {
      case 'soft':
        softDeleteMutation.mutate(deleteTarget.zone.id);
        break;
      case 'hard':
        deleteMutation.mutate(deleteTarget.zone.id);
        break;
      case 'restore':
        restoreMutation.mutate(deleteTarget.zone.id);
        break;
      default:
        break;
    }
    setDeleteTarget(null);
  };

  const handleEdit = (zone) => {
    setSelectedZone(null);
    setEditingZone(zone);
  };

  if (!selectedSite) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon !bg-purple-100">
          <MapIcon className="w-10 h-10 text-purple-600" />
        </div>
        <h3 className="empty-state-title">Select a site</h3>
        <p className="empty-state-description">Choose a site from the dropdown above to view and manage zones.</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Zone Management</h1>
          <p className="page-description">Configure safety zones and PPE requirements</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Zone
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Zones"
          value={zones.length}
          icon={MapIcon}
          color="blue"
          index={0}
        />
        <StatCard
          title="Restricted"
          value={zones.filter(z => z.type === 'restricted').length}
          icon={ExclamationTriangleIcon}
          color="red"
          index={1}
        />
        <StatCard
          title="Current Occupancy"
          value={zonesWithOccupancy.reduce((sum, z) => sum + z.currentOccupancy, 0)}
          icon={UsersIcon}
          color="green"
          index={2}
        />
        <StatCard
          title="Overcrowded"
          value={zonesWithOccupancy.filter(z => z.isOvercrowded).length}
          icon={ExclamationTriangleIcon}
          color="orange"
          index={3}
        />
      </div>

      {/* Show Inactive Zones Toggle */}
      {!isLoading && zones.some(z => !z.isActive) && (
        <div className="glass-card p-4 flex items-center justify-between">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={showInactiveZones}
              onChange={(e) => setShowInactiveZones(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600"
            />
            <span className="text-sm text-slate-600 group-hover:text-slate-800">
              Show inactive zones ({zones.filter(z => !z.isActive).length})
            </span>
          </label>
        </div>
      )}

      {/* All Zones Overview Map */}
      {!isLoading && zonesWithOccupancy.length > 0 && zonesWithOccupancy.some(z => z.isActive && extractPolygonPoints(z.polygon).length >= 3) && (
        <ZonesOverviewMap
          zones={zonesWithOccupancy.filter(z => z.isActive)}
          onSelectZone={(zone) => setSelectedZone(zone)}
        />
      )}

      {/* Zone Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="glass-card p-5 animate-pulse">
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-2">
                  <div className="h-5 w-32 bg-white/10 rounded" />
                  <div className="h-4 w-20 bg-white/10 rounded" />
                </div>
                <div className="w-3 h-3 bg-white/10 rounded-full" />
              </div>
              <div className="flex gap-2 mb-4">
                <div className="h-6 w-24 bg-white/10 rounded-full" />
                <div className="h-6 w-20 bg-white/10 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : zones.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon !bg-purple-100">
            <MapIcon className="w-10 h-10 text-purple-600" />
          </div>
          <h3 className="empty-state-title">No zones configured</h3>
          <p className="empty-state-description">Create zones to define safety requirements for different areas.</p>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />
            Create First Zone
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {zonesWithOccupancy.map((zone, index) => (
            <ZoneCard
              key={zone.id}
              zone={zone}
              index={index}
              onClick={() => setSelectedZone(zone)}
            />
          ))}
        </div>
      )}

      {/* Zone Detail Modal */}
      {selectedZone && (
        <ZoneDetailModal
          zone={selectedZone}
          onClose={() => setSelectedZone(null)}
          onEdit={() => handleEdit(selectedZone)}
          onDelete={() => handleHardDelete(selectedZone)}
          onSoftDelete={() => handleSoftDelete(selectedZone)}
          onRestore={() => handleRestore(selectedZone)}
        />
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingZone) && (
        <ZoneFormModal
          zone={editingZone}
          siteId={selectedSite.id}
          onClose={() => {
            setShowAddModal(false);
            setEditingZone(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.title || 'Delete Zone'}
        message={deleteTarget?.message || ''}
        confirmLabel={deleteTarget?.confirmLabel || 'Delete'}
        variant={deleteTarget?.variant || 'danger'}
      />
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, index }) {
  const colorConfig = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    green: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
  };

  const config = colorConfig[color];

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', config.bg)}>
          <Icon className={clsx('w-5 h-5', config.text)} />
        </div>
      </div>
      <p className="stat-card-value">{value}</p>
      <p className="stat-card-label">{title}</p>
    </div>
  );
}

function ZoneCard({ zone, index, onClick }) {
  const typeInfo = zoneTypes[zone.type] || zoneTypes.general;
  const ppeRequired = zone.ppeRequirements?.required || [];
  const polygonPoints = extractPolygonPoints(zone.polygon);
  const hasPolygon = polygonPoints.length >= 3;

  const riskConfig = {
    low: { border: 'border-l-emerald-500', badge: 'badge-success' },
    medium: { border: 'border-l-amber-500', badge: 'badge-warning' },
    high: { border: 'border-l-orange-500', badge: 'badge-high' },
    critical: { border: 'border-l-red-500', badge: 'badge-critical' },
  };

  const risk = riskConfig[zone.riskLevel] || riskConfig.medium;

  return (
    <div
      onClick={onClick}
      className={clsx(
        'glass-card p-5 cursor-pointer transition-all duration-300 hover:scale-[1.02] border-l-4 animate-fade-in group',
        risk.border,
        zone.isOvercrowded && 'ring-2 ring-red-200',
        !zone.isActive && 'opacity-60 border-slate-300'
      )}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{zone.name}</h3>
            {!zone.isActive && (
              <span className="badge badge-neutral text-xs opacity-70">Inactive</span>
            )}
          </div>
          <p className="text-sm text-slate-500">{zone.code}</p>
        </div>
        <span className={clsx('w-3 h-3 rounded-full', typeInfo.color)} />
      </div>

      {/* Zone Polygon Preview */}
      <div className="mb-4">
        <ZonePolygonPreview
          zone={zone}
          width="100%"
          height={hasPolygon ? 120 : 60}
          showLabel={false}
          className="w-full"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="badge badge-neutral">
          {typeInfo.label}
        </span>
        <span className={clsx('badge', risk.badge)}>
          {zone.riskLevel} risk
        </span>
        {hasPolygon && (
          <span className="badge badge-sm badge-neutral">
            {polygonPoints.length} pts
          </span>
        )}
      </div>

      {/* Occupancy */}
      <div className="flex items-center justify-between text-sm mb-4">
        <span className="text-slate-500">Occupancy</span>
        <span className={clsx(
          'font-semibold',
          zone.isOvercrowded ? 'text-red-600' : 'text-slate-800'
        )}>
          {zone.currentOccupancy} {zone.maxOccupancy ? `/ ${zone.maxOccupancy}` : ''}
        </span>
      </div>

      {/* Required PPE */}
      {ppeRequired.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Required PPE</p>
          <div className="flex flex-wrap gap-1">
            {ppeRequired.map(ppe => (
              <span key={ppe} className="badge badge-sm badge-info">
                {ppe.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ZoneDetailModal({ zone, onClose, onEdit, onDelete, onSoftDelete, onRestore }) {
  const typeInfo = zoneTypes[zone.type] || zoneTypes.general;
  const ppeRequired = zone.ppeRequirements?.required || [];
  const ppeConditional = zone.ppeRequirements?.conditional || [];

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
          {/* Inactive Banner */}
          {!zone.isActive && (
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800">This zone is currently inactive</span>
            </div>
          )}

          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <span className={clsx('w-4 h-4 rounded', typeInfo.color)} />
              <h3 className="modal-title">{zone.name}</h3>
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-body space-y-5">
            {/* Zone Polygon Visualization */}
            <ZonePolygonDetail zone={zone} />

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Code</p>
                <p className="font-medium text-slate-800">{zone.code}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Type</p>
                <p className="font-medium text-slate-800">{typeInfo.label}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Risk Level</p>
                <p className="font-medium text-slate-800 capitalize">{zone.riskLevel}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Max Occupancy</p>
                <p className="font-medium text-slate-800">{zone.maxOccupancy || 'Unlimited'}</p>
              </div>
            </div>

            {/* Current Status */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <h4 className="font-medium text-slate-800 mb-3">Current Status</h4>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Occupancy</span>
                <span className={clsx(
                  'text-2xl font-bold',
                  zone.isOvercrowded ? 'text-red-600' : 'text-emerald-600'
                )}>
                  {zone.currentOccupancy || 0}
                </span>
              </div>
            </div>

            {/* PPE Requirements */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <h4 className="font-medium text-slate-800 mb-3">PPE Requirements</h4>

              {ppeRequired.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-slate-500 mb-2">Required</p>
                  <div className="flex flex-wrap gap-2">
                    {ppeRequired.map(ppe => (
                      <span key={ppe} className="badge badge-critical">
                        {ppe.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {ppeConditional.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Conditional</p>
                  <div className="flex flex-wrap gap-2">
                    {ppeConditional.map(ppe => (
                      <span key={ppe} className="badge badge-warning">
                        {ppe.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {ppeRequired.length === 0 && ppeConditional.length === 0 && (
                <p className="text-slate-500">No specific PPE requirements</p>
              )}
            </div>

            {/* Alert Settings */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <h4 className="font-medium text-slate-800 mb-3">Alert Settings</h4>
              <div className="space-y-3">
                <AlertSetting label="Alert on entry" enabled={zone.alertOnEntry} />
                <AlertSetting label="Alert on PPE violation" enabled={zone.alertOnPPE} />
                <AlertSetting label="Alert on overcrowding" enabled={zone.alertOnOvercrowd} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <div className="flex gap-2">
              {zone.isActive ? (
                <button
                  onClick={onSoftDelete}
                  className="btn btn-secondary text-orange-600 hover:bg-orange-50 flex items-center gap-1"
                >
                  <EyeIcon className="w-4 h-4" />
                  Deactivate
                </button>
              ) : (
                <>
                  <button
                    onClick={onRestore}
                    className="btn btn-secondary text-emerald-600 hover:bg-emerald-50 flex items-center gap-1"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                    Restore
                  </button>
                  <button
                    onClick={onDelete}
                    className="btn btn-secondary text-red-600 hover:bg-red-50 flex items-center gap-1"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                </>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={onClose} className="btn btn-secondary">Close</button>
              {zone.isActive && (
                <button onClick={onEdit} className="btn btn-primary flex items-center gap-1">
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit Zone
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertSetting({ label, enabled }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={clsx(
        'badge badge-sm',
        enabled ? 'badge-success' : 'badge-neutral'
      )}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
    </div>
  );
}
